import { api, API } from "@/api/client";
import { compressImage } from "@/lib/imageCompress";

export const socialApi = {
  directory: (filters = {}) => api.get("/social/directory", { params: filters }).then((r) => r.data),
  friends: () => api.get("/social/friends").then((r) => r.data),
  blocked: () => api.get("/social/friends/blocked").then((r) => r.data),
  followers: () => api.get("/social/followers").then((r) => r.data),
  blockUser: (target_user_id) => api.post("/social/friends/block", { target_user_id }).then((r) => r.data),
  unblock: (target_user_id) => api.post("/social/friends/unblock", { target_user_id }).then((r) => r.data),
  heartbeat: () => api.post("/social/heartbeat").then((r) => r.data),
  notifications: () => api.get("/social/notifications").then((r) => r.data),
  markRead: (category) => api.post("/social/notifications/read", category ? { category } : {}).then((r) => r.data),
  request: (target_user_id) => api.post("/social/request", { target_user_id }).then((r) => r.data),
  respond: (requester_user_id, action) =>
    api.post("/social/respond", { requester_user_id, action }).then((r) => r.data),
  unfriend: (target_user_id) => api.post("/social/unfriend", { target_user_id }).then((r) => r.data),
  updateProfile: (payload) => api.post("/social/profile-update", payload).then((r) => r.data),

  // --- posts / media / relations / privacy ---
  upload: async (file) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    return api.post("/social/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  createPost: (text, media) => api.post("/social/posts", { text, media }).then((r) => r.data),
  shareToFeed: (asset_id, caption = "") => api.post("/social/share-to-feed", { asset_id, caption }).then((r) => r.data),
  userPosts: (owner) => api.get("/social/posts", { params: { owner } }).then((r) => r.data),
  feed: () => api.get("/social/feed").then((r) => r.data),
  globalFeed: (skip = 0, limit = 10) =>
    api.get("/social/global-feed", { params: { skip, limit } }).then((r) => r.data),
  notInterested: (post_id) => api.post("/social/not-interested", { post_id }).then((r) => r.data),
  block: (target_user_id) => api.post("/social/block", { target_user_id }).then((r) => r.data),
  follow: (target_user_id) => api.post("/social/follow", { target_user_id }).then((r) => r.data),
  subscribe: (target_user_id) => api.post("/social/subscribe", { target_user_id }).then((r) => r.data),
  setPrivacy: (privacy) => api.post("/social/privacy", { privacy }).then((r) => r.data),
  targetProfile: (id) => api.get(`/social/profile/${id}`).then((r) => r.data),

  // --- engagement + combined owner feed ---
  followersCount: () => api.get("/social/followers-count").then((r) => r.data),
  myFeed: (offset = 0, limit = 10) =>
    api.get("/social/myfeed", { params: { offset, limit } }).then((r) => r.data),
  like: (item_id, item_type = "post") =>
    api.post("/social/like", { item_id, item_type }).then((r) => r.data),
  comments: (item_id, item_type = "post") =>
    api.get("/social/comments", { params: { item_id, item_type } }).then((r) => r.data),
  addComment: (item_id, text, item_type = "post") =>
    api.post("/social/comment", { item_id, text, item_type }).then((r) => r.data),
  editItem: (item_id, text, item_type = "post") =>
    api.post("/social/item/edit", { item_id, text, item_type }).then((r) => r.data),
  hideItem: (item_id, item_type = "post") =>
    api.post("/social/item/hide", { item_id, item_type }).then((r) => r.data),
  deleteItem: (item_id, item_type = "post") =>
    api.post("/social/item/delete", { item_id, item_type }).then((r) => r.data),
};

// Media is served through the backend; <img>/<audio> can't send headers, so
// pass the JWT as a query param.
export const mediaUrl = (id, token) => `${API}/social/media/${id}?auth=${token || ""}`;

// Resolve a stored profile media reference (absolute URL, /api/... relative, or
// a local blob: preview) into a directly renderable <img src>.
export const resolveMedia = (url, token) => {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${process.env.REACT_APP_BACKEND_URL}${url}${sep}auth=${token || ""}`;
  }
  return url;
};
